import { Component, type ReactNode } from "react";

/**
 * Last-resort error boundary around the route tree. Without it, any render
 * error white-screens the whole SPA with nothing but a console trace. With
 * it, the user gets a recover path (reload / go home) and we keep the
 * error in the console for diagnosis.
 *
 * Class component because error boundaries still require the class API.
 */
export class ErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error) {
    console.error("Route render error:", error);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="grid min-h-screen place-items-center px-4">
        <div className="max-w-md text-center">
          <p className="kicker">Something broke</p>
          <h1 className="display mt-2 text-2xl text-fg-0">
            This page hit an unexpected error
          </h1>
          <p className="mt-2 text-sm leading-relaxed text-fg-2">
            Your data is fine; the page just failed to render. Reloading
            usually clears it.
          </p>
          <div className="mt-6 flex items-center justify-center gap-2">
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="btn-primary"
            >
              Reload
            </button>
            <a href="/" className="btn-secondary">
              Go home
            </a>
          </div>
        </div>
      </main>
    );
  }
}
